import {
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon
} from "@mui/icons-material";
import { Box, Paper, Stack, Typography, Tooltip } from "@mui/material";
import {
  ProgressButton,
  ASTableV2,
  ASTableV2Body,
  ASTableV2Cell,
  ASTableV2Header,
  ASTableV2Row,
  ASTableV2RowCollapse,
  ASTableV2RowEmpty
} from "@qubit/autoparts";
import { skipToken } from "@reduxjs/toolkit/query";
import { t } from "i18next";
import { Fragment, useMemo } from "react";

import { useAppDispatch, useAppSelector } from "~/app/store";

import { groupBy } from "~/lib/shared";
import { selectStatusSelectedAutostoreGridId } from "~/redux/selectors/statusSelectors";
import {
  useDeleteTaskGroupMutation,
  useDeleteTaskMutation,
  useGetAutostoreTasksQuery
} from "~/redux/warehouse/autostoreGrid.hooks";
import { AutostoreTaskGroupInfoExtended } from "~/types/api";

import { setSelectedTaskGroupId, setTaskGroupSort } from "./status.slice";

// Format category description for consistent display
// Handles: "all" → "All", "FlaggedBinInventoryMoved" → "Flagged Bin Inventory Moved"
const formatCategoryDescription = (desc: string): string => {
  if (!desc) return desc;
  // Split camelCase and capitalize each word
  const words = desc.replace(/([a-z])([A-Z])/g, "$1 $2").split(" ");
  return words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

type SortField =
  | "taskGroupId"
  | "selectedPortId"
  | "category"
  | "shipment"
  | "batchName"
  | "batchStatus"
  | "tasks";
type SortDirection = "asc" | "desc";

const Row = ({ row }: { row: AutostoreTaskGroupInfoExtended }) => {
  const selectedGridId = useAppSelector(selectStatusSelectedAutostoreGridId);
  const selected = useAppSelector(
    (state) => state.status.selectedTaskGroupId === row.taskGroupId
  );
  const dispatch = useAppDispatch();
  const [cancelTaskGroup] = useDeleteTaskGroupMutation();
  const [cancelTask] = useDeleteTaskMutation();

  return (
    <Fragment key={row.taskGroupId}>
      <ASTableV2Row
        expanded={selected}
        onClick={() =>
          dispatch(
            setSelectedTaskGroupId(selected ? undefined : row.taskGroupId)
          )
        }
      >
        <ASTableV2Cell>
          <Typography variant="tableBody">{row.taskGroupId || ""}</Typography>
        </ASTableV2Cell>
        <ASTableV2Cell>
          <Typography variant="tableBody">
            {row.selectedPortId || ""}
          </Typography>
        </ASTableV2Cell>
        <ASTableV2Cell>
          <Typography variant="tableBody">
            {row.categoryDescription
              ? `${formatCategoryDescription(row.categoryDescription)} - ${row.category}`
              : row.category || ""}
          </Typography>
        </ASTableV2Cell>
        <ASTableV2Cell>
          <Typography variant="tableBody">{row.shipment || ""}</Typography>
        </ASTableV2Cell>
        <ASTableV2Cell>
          <Typography variant="tableBody">{row.batchName || ""}</Typography>
        </ASTableV2Cell>
        <ASTableV2Cell>
          <Typography variant="tableBody">{row.batchStatus || ""}</Typography>
        </ASTableV2Cell>
        <ASTableV2Cell>
          <Typography variant="tableBody">{row.tasks.length || ""}</Typography>
        </ASTableV2Cell>
        <ASTableV2Cell>
          <ProgressButton
            buttonSize="small"
            emphasis="low"
            responsive
            variant="subtle"
            color="primary"
            aria-label={`cancel-task-group-id-${row.taskGroupId}`}
            onClick={async (): Promise<void> => {
              if (!selectedGridId) return;

              await cancelTaskGroup({
                taskGroupId: row.taskGroupId,
                autostoreGridId: selectedGridId
              });
            }}
            style={{
              marginRight: 10
            }}
          >
            {t("cancel task group")}
          </ProgressButton>
        </ASTableV2Cell>
      </ASTableV2Row>
      <ASTableV2RowCollapse expanded={selected}>
        <Stack
          direction="row"
          flexWrap={"wrap"}
          gap={2}
          justifyContent="space-between"
        >
          {Object.entries(groupBy(row.tasks, (t) => `${t.binId}`)).map(
            ([binId, tasks]) => (
              <Paper key={binId} sx={{ p: 2 }}>
                <Typography variant="tableBody">Bin Id: {binId}</Typography>
                <Box
                  display="grid"
                  gridTemplateColumns="repeat(auto-fill, minmax(350px, 1fr))"
                  gap={2}
                  my={2}
                >
                  {tasks.map((task) => (
                    <Box
                      key={task.taskId}
                      display="flex"
                      gap={2}
                      alignItems="center"
                    >
                      <Typography variant="tableBody">
                        Task Id: {task.taskId}
                      </Typography>
                      <ProgressButton
                        buttonSize="small"
                        emphasis="high"
                        responsive
                        variant="subtle"
                        color="primary"
                        aria-label={`cancel-task-id-${task.taskId}`}
                        onClick={async (): Promise<void> => {
                          if (!selectedGridId) return;

                          await cancelTask({
                            taskId: task.taskId,
                            autostoreGridId: selectedGridId
                          });
                        }}
                      >
                        {t("cancel task")}
                      </ProgressButton>
                    </Box>
                  ))}
                </Box>
              </Paper>
            )
          )}
        </Stack>
      </ASTableV2RowCollapse>
    </Fragment>
  );
};

interface SortableHeaderProps {
  label: string;
  field: SortField;
  sortField: SortField | null;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
}

const SortableHeader = ({
  label,
  field,
  sortField,
  sortDirection,
  onSort
}: SortableHeaderProps) => {
  const isActive = sortField === field;

  return (
    <ASTableV2Cell>
      <Tooltip title={`Sort by ${label}`}>
        <Box
          onClick={() => onSort(field)}
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            userSelect: "none",
            "&:hover": { opacity: 0.7 }
          }}
        >
          <Typography variant="tableBody">{label}</Typography>
          {isActive && (
            <Box sx={{ ml: 0.5, display: "flex", alignItems: "center" }}>
              {sortDirection === "asc" ? (
                <ArrowUpIcon fontSize="small" />
              ) : (
                <ArrowDownIcon fontSize="small" />
              )}
            </Box>
          )}
        </Box>
      </Tooltip>
    </ASTableV2Cell>
  );
};

export const TaskGroupTableByTaskGroup = () => {
  const dispatch = useAppDispatch();
  const selectedGridId = useAppSelector(selectStatusSelectedAutostoreGridId);
  const sortField = useAppSelector(
    (state) => state.status.taskGroupSortField as SortField | null
  );
  const sortDirection = useAppSelector(
    (state) => state.status.taskGroupSortDirection as SortDirection
  );

  const { data: autostoreTasks, isLoading: autostoreTasksIsLoading } =
    useGetAutostoreTasksQuery(selectedGridId || skipToken);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if same field
      const newDirection = sortDirection === "asc" ? "desc" : "asc";
      dispatch(setTaskGroupSort({ field, direction: newDirection }));
    } else {
      // Set new field, default to ascending
      dispatch(setTaskGroupSort({ field, direction: "asc" }));
    }
  };

  const sortedTasks = useMemo(() => {
    if (!autostoreTasks || !sortField) return autostoreTasks || [];

    return [...autostoreTasks].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case "taskGroupId":
          comparison = (a.taskGroupId || 0) - (b.taskGroupId || 0);
          break;
        case "selectedPortId":
          comparison = (a.selectedPortId || 0) - (b.selectedPortId || 0);
          break;
        case "category":
          comparison = (a.category || 0) - (b.category || 0);
          break;
        case "shipment":
          comparison = (a.shipment || 0) - (b.shipment || 0);
          break;
        case "batchName":
          comparison = (a.batchName || "").localeCompare(b.batchName || "");
          break;
        case "batchStatus":
          comparison = (a.batchStatus || "").localeCompare(b.batchStatus || "");
          break;
        case "tasks":
          comparison = (a.tasks?.length || 0) - (b.tasks?.length || 0);
          break;
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [autostoreTasks, sortField, sortDirection]);

  return (
    <ASTableV2
      gridTemplateColumns="repeat(8, auto)"
      isLoading={autostoreTasksIsLoading}
    >
      <ASTableV2Header>
        <ASTableV2Row>
          <SortableHeader
            label="TaskGroupId"
            field="taskGroupId"
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          />
          <SortableHeader
            label="Port"
            field="selectedPortId"
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          />
          <SortableHeader
            label="Category"
            field="category"
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          />
          <SortableHeader
            label="Shipment"
            field="shipment"
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          />
          <SortableHeader
            label="Batch Name"
            field="batchName"
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          />
          <SortableHeader
            label="Batch Status"
            field="batchStatus"
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          />
          <SortableHeader
            label="Tasks"
            field="tasks"
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          />
          <ASTableV2Cell></ASTableV2Cell>
        </ASTableV2Row>
      </ASTableV2Header>
      <ASTableV2Body>
        {!autostoreTasksIsLoading && !sortedTasks?.length && (
          <ASTableV2RowEmpty>{t("no task groups found")}</ASTableV2RowEmpty>
        )}
        {sortedTasks.map((row) => (
          <Row key={row.taskGroupId} row={row} />
        ))}
      </ASTableV2Body>
    </ASTableV2>
  );
};
